import React from 'react';

/**
 * Table-fill contract (bd-n3uyh, canonicalized in BlueprintsTable.tsx:47):
 * a full page of results (current filter's total >= the page size) FILLS
 * its container down to the in-flow pagination below (flex-1) and scrolls
 * internally; a sparse result set HUGS its content height (max-h-full) so
 * a few rows never stretch into a tall empty box.
 *
 * Shared by every paginated table (deepcontext-wbra) so the
 * flex-1/max-h-full + min-h-0 + overflow-y-auto chain isn't copy-pasted
 * (and silently dropped a piece at a time) per page.
 */
export function fillOrHugClass(isFullPage: boolean): 'flex-1' | 'max-h-full' {
    return isFullPage ? 'flex-1' : 'max-h-full';
}

/**
 * The other half of the contract: on a full page, the `<table>` element
 * itself (not just its scroll box) needs `h-full` too. Auto-layout tables
 * distribute any extra block-size beyond their rows' natural height out
 * across those rows (CSS2.1 17.5.3) — that's what makes a short page of
 * rows visually stretch edge-to-edge instead of leaving a dead gap below
 * the last row inside an otherwise-full box. Without this, TableFillBox
 * alone still fills the box but leaves that dead gap.
 */
export function tableFillHeightClass(isFullPage: boolean): 'h-full' | '' {
    return isFullPage ? 'h-full' : '';
}

interface TableFillBoxProps extends React.HTMLAttributes<HTMLDivElement> {
    isFullPage: boolean;
    /**
     * Optional pagination (or other) footer, pinned inside the card below
     * the scrollable table instead of floating below it (deepcontext-c0p7.3
     * — the locked table-card spec puts pagination in a
     * `border-t border-line bg-ghost-bg` footer INSIDE the card). When
     * given, the table content moves into its own scrolling sub-box so the
     * footer stays put; omitting it keeps the original single scrolling
     * div, unchanged for every caller that doesn't need a footer yet.
     */
    footer?: React.ReactNode;
    children: React.ReactNode;
}

/**
 * The canonical single-div box that wraps a paginated table: sizes itself
 * per `fillOrHugClass` and scrolls its own content internally rather than
 * letting the page scroll past it. Composes with a caller-supplied
 * `className` for the box's visual chrome (border/bg/rounding) since that
 * varies (e.g. a box nested inside an already-chromed card shouldn't
 * re-add its own border).
 */
export const TableFillBox: React.FC<TableFillBoxProps> = ({
    isFullPage,
    footer,
    className = '',
    children,
    ...rest
}) => {
    if (!footer) {
        return (
            <div className={`${fillOrHugClass(isFullPage)} min-h-0 overflow-y-auto ${className}`} {...rest}>
                {children}
            </div>
        );
    }

    return (
        <div className={`${fillOrHugClass(isFullPage)} min-h-0 flex flex-col overflow-hidden ${className}`} {...rest}>
            <div className="min-h-0 flex-1 overflow-y-auto">
                {children}
            </div>
            <div className="flex-shrink-0 border-t border-line bg-ghost-bg px-4 py-2.5">
                {footer}
            </div>
        </div>
    );
};
